"use client";

import type { ArticleSection } from "@/generated/sb";
import { ArticleSection as ArticleSectionUI } from "@repo/ui-parkmobile/ArticleSection";
import {
  imageStoryblokResolver,
  titleResolver,
  listResolver,
  automatedArticleRichtextResolver,
} from "@/lib/storyblok/adaptors";
import { SimpleRichText } from "./SimpleRichText";
import { EmbeddedHTML } from "./EmbeddedHTML";
import {
  calculateReadTimeFromContent,
  formatPublicationDate,
} from "@/lib/article";
import { storyblokEditable } from "@storyblok/react/rsc";
import React from "react";

interface ArticleSectionProps {
  blok: ArticleSection;
  publicationDate?: string;
}

export default function ArticleSection({ blok, publicationDate }: ArticleSectionProps) {
  if (!blok) {
    return null;
  }

  // Process content items
  const content = blok.content
    ?.map((item, index) => {
      switch (item.component) {
        case "image": {
          const imageData = imageStoryblokResolver(item);
          if (!imageData) return null;
          return {
            type: "image" as const,
            data: {
              ...imageData,
              aspectRatio: imageData.aspectRatio?.toString() || "16/9",
              ...storyblokEditable(item),
            },
          };
        }

        case "title": {
          const titleData = titleResolver([item]);
          if (!titleData) return null;
          return {
            type: "title" as const,
            data: {
              ...titleData,
              ...storyblokEditable(item),
            },
          };
        }

        case "simpleRichText": {
          // Return a component that will be rendered in the UI
          return {
            type: "simpleRichText" as const,
            data: {
              children: (
                <SimpleRichText blok={item} key={`richtext-${index}`} />
              ),
              ...storyblokEditable(item),
            },
          };
        }

        case "list": {
          const listData = listResolver([item]);
          if (!listData) return null;
          return {
            type: "list" as const,
            data: {
              ...listData,
              ...storyblokEditable(item),
            },
          };
        }

        case "automatedArticleRichtext": {
          const richtextData = automatedArticleRichtextResolver(item);
          if (!richtextData) return null;
          return {
            type: "automatedArticleRichtext" as const,
            data: richtextData,
          };
        }

        case "embeddedHTML": {
          return {
            type: "embeddedHTML" as const,
            data: {
              children: (
                <EmbeddedHTML blok={item} key={`embedded-html-${index}`} />
              ),
              ...storyblokEditable(item),
            },
          };
        }

        default:
          return null;
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  // Calculate read time from content
  const readTime = calculateReadTimeFromContent(blok.content);

  // Format publication date - use passed prop or fallback to blok's publicationDate
  const formattedDate = formatPublicationDate(publicationDate || blok.publicationDate);

  // Create metadata object
  const metadata = {
    readTime,
    publishedDate: formattedDate,
  };

  return (
    <ArticleSectionUI
      {...storyblokEditable(blok)}
      backgroundColor={blok.backgroundColor || "none"}
      topPadding={blok.topPadding || "none"}
      bottomPadding={blok.bottomPadding || "none"}
      content={content || []}
      metadata={metadata}
      layout={blok.layout === "full" ? "full" : "right"}
      id={blok.anchor}
    />
  );
}
